import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
} from '@forgenetwork/ui';
import { TeamMemberList, InviteMemberModal } from '@/components/team';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { api } from '@/lib/api';
import { Users, UserPlus, Mail } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/team')({
  component: TeamPage,
});

interface Member {
  userId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: string | null;
  invitedAt: string;
  invitedBy: string | null;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

interface Workspace {
  id: string;
  name: string;
  slug: string;
  ownerId: string;
  memberCount: number;
  userRole: 'owner' | 'admin' | 'member' | 'viewer';
}

function TeamPage(): React.JSX.Element {
  const { user } = useAuth();
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<string | null>(null);

  // Fetch workspaces
  const { data: workspacesData } = useQuery<{ workspaces: Workspace[] }>({
    queryKey: ['workspaces'],
    queryFn: async () => api.get('/workspaces'),
  });

  const workspace = workspacesData?.workspaces?.[0];

  React.useEffect(() => {
    if (workspace?.id) {
      setSelectedWorkspaceId(workspace.id);
    }
  }, [workspace]);

  // Fetch members (with user details)
  // NOTE: Backend should be updated to include user details in the response
  // For now, we'll use member data with placeholder user info
  const { data: membersData, isLoading: membersLoading } = useQuery<{ members: Member[] }>({
    queryKey: ['workspace', selectedWorkspaceId, 'members'],
    queryFn: async () => api.get(`/workspaces/${selectedWorkspaceId}/members`),
    enabled: !!selectedWorkspaceId,
  });

  // Fetch invitations
  const { data: invitationsData } = useQuery<{ invitations: Invitation[] }>({
    queryKey: ['workspace', selectedWorkspaceId, 'invitations'],
    queryFn: async () => api.get(`/workspaces/${selectedWorkspaceId}/invitations`),
    enabled: !!selectedWorkspaceId && (workspace?.userRole === 'owner' || workspace?.userRole === 'admin'),
  });

  const canInviteMembers = workspace?.userRole === 'owner' || workspace?.userRole === 'admin';

  // Enrich members with placeholder user data
  // TODO: Update backend to include user details in members response
  const enrichedMembers = React.useMemo(() => {
    if (!membersData?.members) return [];

    return membersData.members.map((member) => ({
      ...member,
      user: {
        id: member.userId,
        name: member.userId === user?.id ? user.name : `User ${member.userId.slice(0, 8)}`,
        email: member.userId === user?.id ? user.email : `user-${member.userId.slice(0, 8)}@example.com`,
        avatarUrl: null,
      },
    }));
  }, [membersData, user]);

  const pendingInvitations = invitationsData?.invitations?.filter(
    (inv) => inv.status === 'pending'
  ) ?? [];

  if (!workspace) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>No Workspace Found</CardTitle>
              <CardDescription>
                You need to create a workspace first to manage team members.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Team</h1>
            <p className="text-muted-foreground">
              Manage members and invitations for {workspace.name}
            </p>
          </div>
          {canInviteMembers && (
            <Button onClick={() => setInviteModalOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <div className="text-2xl font-bold">{membersData?.members?.length ?? 0}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Invitations</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <div className="text-2xl font-bold">{pendingInvitations.length}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Your Role</CardTitle>
              <Badge variant="default">
                {workspace.userRole ? workspace.userRole.charAt(0).toUpperCase() + workspace.userRole.slice(1) : 'Unknown'}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                {!workspace.userRole
                  ? 'Role not assigned'
                  : workspace.userRole === 'owner'
                  ? 'Full access and control'
                  : workspace.userRole === 'admin'
                  ? 'Can manage members'
                  : workspace.userRole === 'member'
                  ? 'Can collaborate'
                  : 'Read-only access'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Members List */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>
              Manage roles and permissions for workspace members
            </CardDescription>
          </CardHeader>
          <CardContent>
            {membersLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading members...</div>
            ) : (
              <TeamMemberList
                workspaceId={workspace.id}
                members={enrichedMembers}
                currentUserId={user?.id ?? ''}
                currentUserRole={workspace.userRole}
              />
            )}
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        {canInviteMembers && pendingInvitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Pending Invitations</CardTitle>
              <CardDescription>
                Invitations waiting to be accepted
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingInvitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <div className="font-medium">{invitation.email}</div>
                      <div className="text-sm text-muted-foreground">
                        Invited as {invitation.role} •{' '}
                        {new Date(invitation.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge variant="outline">{invitation.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Invite Modal */}
      {selectedWorkspaceId && (
        <InviteMemberModal
          workspaceId={selectedWorkspaceId}
          open={inviteModalOpen}
          onOpenChange={setInviteModalOpen}
        />
      )}
    </div>
  );
}
